import type { IntlShape } from 'react-intl'
import { defineMessages } from 'react-intl'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'

type PrizeKind = 'grandPrize'

export interface Prize {
  key: string
  title: string
  kind: PrizeKind
  type: string
  image: App.Image
  description: string
}

interface PrizeDefinition {
  key: string
  titleKey: keyof typeof messages
  kind: PrizeKind
  image: App.Image
  descriptionKey: keyof typeof messages
}

const messages = defineMessages({
  grandPrizeType: {
    id: '0XT4rqidHCxBF9UtqxSt',
    defaultMessage: 'Grand Prize',
    description: 'Label for grand prize entries in the Lux Award prize modal',
  },
  runnerUpPrizeAu: {
    id: 'vBnNjG0Mog7Oj_Ng9SRw',
    defaultMessage: '{creditValue} credit to use towards your next escape for 300 lucky winners!*',
    description: 'Runner-up prize description for the Lux Award modal in Australia',
  },
  runnerUpPrizeNz: {
    id: '6q8VkY6kqdTj6hA6lxaa',
    defaultMessage: '{creditValue} credit to use towards your next escape for 100 lucky winners!*',
    description: 'Runner-up prize description for the Lux Award modal in New Zealand',
  },
  runnerUpPrizeUs: {
    id: 'T44bOHL6SxjEvncMB9jf',
    defaultMessage: '{creditValue} credit to use towards your next escape for 50 lucky winners!*',
    description: 'Runner-up prize description for the Lux Award modal in the United States',
  },
  runnerUpPrizeGb: {
    id: '7xeydN446QMuQ5J6ej7l',
    defaultMessage: '{creditValue} credit to use towards your next escape for 50 lucky winners!*',
    description: 'Runner-up prize description for the Lux Award modal in Great Britain',
  },
  manaIslandDescription: {
    id: 'AIQBq0depcNW-3NsuRqu',
    defaultMessage: `**5 nights in a Deluxe Ocean View Bure**
- Guaranteed double upgrade from an Island Bure to Deluxe Ocean View Bure
- Two children (14 and under) stay free, sleeping on existing bedding, with all dining inclusions and Kids' Club access
- Daily buffet breakfast at Mamanuca Restaurant
- Daily buffet lunch at Mamanuca Restaurant
- Nightly buffet dinner at Mamanuca Restaurant
- Daily drink at Vonu Bar or the swim-up pool bar
- {resortCredit} credit to use on all bars, restaurants and activities
- Complimentary use of non-motorised water equipment
- Complimentary Wi-Fi for up to two devices
- Inclusions valid for two guests`,
    description: 'Prize details for the Mana Island Resort and Spa grand prize in the Lux Award modal',
  },
  shangriLaDescription: {
    id: '40QrLwPK6n8utE-f5wfX',
    defaultMessage: `**5 nights in a Garden Wing Deluxe Sea View**
- Guaranteed upgrade from Superior Rainforest Room to Deluxe Seaview Room
- Daily buffet breakfast at Coffee Terrace
- One set-menu three-course lunch at Oceano
- One buffet dinner at Tepi Laut or Coffee Terrace
- Daily beer or glass of wine per adult at Sampan Bar
- One-way private airport transfer
- One 30-minute head and shoulder massage per adult at The Spa
- One sunrise hike per package
- Daily scheduled shuttle bus services from the hotel to Kota Kinabalu City
- Unlimited non-motorised watersports equipment hire
- Unlimited access to Cool Zone Kids Club for two children
- Access to the Discovery Centre, onsite forest garden and waterfall
- Inclusions valid for two guests`,
    description: 'Prize details for the Shangri-La Rasa Ria grand prize in the Lux Award modal',
  },
  essencePeregianDescription: {
    id: 'cyojAnDoI7ZiLmRnQokM',
    defaultMessage: `**5 nights in a Three-Bedroom House**
- Holiday house welcome hamper of snacks and drinks
- Welcome bottle of wine in house on arrival
- Self-parking for up to two vehicles per package
- Complimentary Wi-Fi
- Inclusions valid for six guests`,
    description: 'Prize details for the Essence Peregian Beach grand prize in the Lux Award modal',
  },
  sandsKhaoLakDescription: {
    id: 'Sto33FZv_M1p2rCa9ORs',
    defaultMessage: `**8 nights in the Sands Room**
- Daily breakfast at The Floating Market
- Two three-course set-menu lunches or dinners per person
- Two 60-minute massages per adult at Beachfront Sala Thai Massages
- The Sands Luxury Club access with daily free-flow drinks hour
- One-way private airport transfer on arrival
- Inclusions valid for two guest`,
    description: 'Prize details for The Sands Khao Lak by Katathani grand prize in the Lux Award modal',
  },
  hawaiiCruiseDescription: {
    id: 'DF6oUHfwOepG-Y9bWtM1',
    defaultMessage: `**10 nights in a Twin Room - Inside Stateroom**
- Packages valid for one person (twin room) – you MUST purchase TWO packages
- Seven nights cruising onboard Norwegian Cruise Line’s Pride of America in an Inside Stateroom
- Two nights of pre-cruise accommodation and one night of post-cruise accommodation in an Ocean View Room at OUTRIGGER Waikiki Beach Resort, with breakfast
- All-inclusive dining, with daily breakfast, lunch and dinner in select restaurants onboard
- One Specialty Dining meal at select onboard bars, restaurants and eateries
- Premium Drinks package, including spirits, cocktails, beer and wine onboard (valued up to {drinksPackageValue})
- {shoreExcursionCredit} shore excursion credit per stateroom in each port (valued up to {shoreExcursionValue})
- 300 minutes of Wi-Fi per cabin across two devices
- All port taxes, service charges, gratuities and onboard entertainment`,
    description: 'Prize details for the Hawaii cruise grand prize in the Lux Award modal',
  },
  apurvaKempinskiDescription: {
    id: 'VF6_JHEZOcaO98wB8r4H',
    defaultMessage: `**5 nights in a Cliff Private pool Junior Suite**
- Daily breakfast at Cliff Lounge or Pala Restaurant
- Daily choice of lunch or dinner at Reef Beach Club, Bai Yun, Izakaya by Oku or Pala Restaurant
- Daily access to the Cliff Lounge with refreshments
- Nightly free-flow drinks hour with canapes at Pala Restaurant or Cliff Lounge
- One 50-minute massage treatment at Apurva Spa per adult
- Signature welcome drink per person
- VIP check-in and checkout and dedicated concierge service for Suite and Villa guests
- Daily Kids’ Club access at Jalak Family Club
- Daily resort activities based on resort schedule
- Inclusions valid for two guests`,
    description: 'Prize details for The Apurva Kempinski Bali grand prize in the Lux Award modal',
  },
  manaIslandTitle: {
    id: 'i3OiKVbZWgxMjC3YWIme',
    defaultMessage: 'Mana Island Resort and Spa',
    description: 'Prize title for the Mana Island Resort and Spa grand prize in the Lux Award modal',
  },
  shangriLaTitle: {
    id: 'GGv3R8ip0kzYVLY4bKDD',
    defaultMessage: 'Shangri-La Rasa Ria, Kota Kinabalu',
    description: 'Prize title for the Shangri-La Rasa Ria grand prize in the Lux Award modal',
  },
  essencePeregianTitle: {
    id: 'cI36UW9ddQ6Ll4emDewM',
    defaultMessage: 'Essence Peregian Beach – Boutique Hotel & Holiday Homes',
    description: 'Prize title for the Essence Peregian Beach grand prize in the Lux Award modal',
  },
  sandsKhaoLakTitle: {
    id: 'LVGOrsURmE2WA12kVFJA',
    defaultMessage: 'The Sands Khao Lak by Katathani',
    description: 'Prize title for The Sands Khao Lak by Katathani grand prize in the Lux Award modal',
  },
  hawaiiCruiseTitle: {
    id: '0z5ozpucMLJyVYxCcniS',
    defaultMessage: 'Hawaii: Pride of America Island Hopping Cruise with Waikiki Outrigger Stays & Bonus Premium Drinks',
    description: 'Prize title for the Hawaii Pride of America cruise grand prize in the Lux Award modal',
  },
  apurvaKempinskiTitle: {
    id: 'pv7WNFhvCy2qaJPxOwVd',
    defaultMessage: 'The Apurva Kempinski Bali',
    description: 'Prize title for The Apurva Kempinski Bali grand prize in the Lux Award modal',
  },
})

const PRIZE_DEFINITIONS: Array<PrizeDefinition> = [
  {
    key: 'mana-island-resort-and-spa',
    titleKey: 'manaIslandTitle',
    kind: 'grandPrize',
    image: { id: '2sbduzt4vger4jcuczz' },
    descriptionKey: 'manaIslandDescription',
  },
  {
    key: 'shangri-la-rasa-ria-kota-kinabalu',
    titleKey: 'shangriLaTitle',
    kind: 'grandPrize',
    image: { id: '3paydqkuxphe1udys8gn' },
    descriptionKey: 'shangriLaDescription',
  },
  {
    key: 'essence-peregian-beach',
    titleKey: 'essencePeregianTitle',
    kind: 'grandPrize',
    image: { id: 's25km2d43rsor0aehh' },
    descriptionKey: 'essencePeregianDescription',
  },
  {
    key: 'the-sands-khao-lak-by-katathani',
    titleKey: 'sandsKhaoLakTitle',
    kind: 'grandPrize',
    image: { id: '0sei2xqm09h4ao6uo57l' },
    descriptionKey: 'sandsKhaoLakDescription',
  },
  {
    key: 'hawaii-pride-of-america-cruise',
    titleKey: 'hawaiiCruiseTitle',
    kind: 'grandPrize',
    image: { id: 'nruhp3lclj7ry2iad0yg' },
    descriptionKey: 'hawaiiCruiseDescription',
  },
  {
    key: 'the-apurva-kempinski-bali',
    titleKey: 'apurvaKempinskiTitle',
    kind: 'grandPrize',
    image: { id: 'hdifik3u5frxij87k9d' },
    descriptionKey: 'apurvaKempinskiDescription',
  },
]

export const LUX_AWARD_PRIZE_KEYS = PRIZE_DEFINITIONS.map((prize) => prize.key)

const runnerUpPrizeValues = {
  AU: { creditValue: formatCurrency(100, 'AUD', 'AU', { format: 'roundedDollar' }) },
  NZ: { creditValue: formatCurrency(100, 'NZD', 'NZ', { format: 'roundedDollar' }) },
  US: { creditValue: formatCurrency(250, 'USD', 'US', { format: 'roundedDollar' }) },
  GB: { creditValue: formatCurrency(250, 'GBP', 'GB', { format: 'roundedDollar' }) },
}

const prizeDescriptionValues: Partial<Record<PrizeDefinition['descriptionKey'], Record<string, string>>> = {
  manaIslandDescription: {
    resortCredit: formatCurrency(300, 'FJD', 'AU', { format: 'roundedDollar' }),
  },
  hawaiiCruiseDescription: {
    drinksPackageValue: formatCurrency(1167, 'AUD', 'AU', { format: 'roundedDollar' }),
    shoreExcursionCredit: formatCurrency(50, 'USD', 'US', { format: 'roundedDollar' }),
    shoreExcursionValue: formatCurrency(200, 'USD', 'US', { format: 'roundedDollar' }),
  },
}

export const getLuxAwardRunnerUpPrizes = (intl: IntlShape): Record<string, string> => ({
  AU: intl.formatMessage(messages.runnerUpPrizeAu, runnerUpPrizeValues.AU),
  NZ: intl.formatMessage(messages.runnerUpPrizeNz, runnerUpPrizeValues.NZ),
  US: intl.formatMessage(messages.runnerUpPrizeUs, runnerUpPrizeValues.US),
  GB: intl.formatMessage(messages.runnerUpPrizeGb, runnerUpPrizeValues.GB),
})

export const getLuxAwardPrizes = (intl: IntlShape): Array<Prize> => PRIZE_DEFINITIONS.map((prize) => ({
  key: prize.key,
  title: intl.formatMessage(messages[prize.titleKey]),
  kind: prize.kind,
  type: intl.formatMessage(messages.grandPrizeType),
  image: prize.image,
  description: intl.formatMessage(messages[prize.descriptionKey], prizeDescriptionValues[prize.descriptionKey]),
}))
